import {
    CheckCircle2,
    Loader2,
    Mail,
    MessageCircle,
    Plus,
    Rss,
    Users,
} from 'lucide-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import Select from 'react-select'
import { toast } from 'react-toastify'
import DRCEachReceiver from '../../../Components/Settings/DRCEachReceiver'
import { countryCodesHolder } from '../../../Helper/countryCodes'
import { BASE_URL_API } from '../../../References/URLs'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import AuthContext from '../../../store/auth-context'
import styles from './DailyReportsConfiguration.module.css'

const PLATFORMS = [
    {
        title: 'WhatsApp',
        icon: FaWhatsapp,
        color: '#25D366',
        bg: '#f0fdf4',
        border: '#bbf7d0',
        activeBg: '#dcfce7',
        activeBorder: '#22c55e',
        activeColor: '#15803d',
        description: 'Send daily reports to WhatsApp numbers',
    },
    {
        title: 'E-mail',
        icon: Mail,
        color: '#2463EB',
        bg: '#eff6ff',
        border: '#bfdbfe',
        activeBg: '#dbeafe',
        activeBorder: '#3b82f6',
        activeColor: '#1d4ed8',
        description: 'Deliver reports straight to email inboxes',
    },
]

const DailyReportsConfiguration = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== '' ? JSON.parse(asCtx.activeStore) : null } catch { return null } })()

    const [activePlatform, setActivePlatform] = useState(null)
    const [isFetching, setIsFetching] = useState(false)
    const [receiversData, setReceiversData] = useState([])
    const [isAdding, setIsAdding] = useState(false)
    const [receiver, setReceiver] = useState('')
    const [countryCode, setCountryCode] = useState({ value: '+91', label: '+91 India' })

    const countryCodeOptions = useMemo(() =>
        countryCodesHolder.map((cc) => ({ value: cc.code, label: `${cc.code} ${cc.country}` }))
    , [])

    const ccSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '40px',
            height: '40px',
            borderRadius: '8px',
            border: `1.5px solid ${state.isFocused ? '#2463EB' : '#e2e8f0'}`,
            boxShadow: 'none',
            background: state.isFocused ? '#fff' : '#f8fafc',
            cursor: 'pointer',
            fontSize: '0.8rem',
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: '#2463EB' },
        }),
        valueContainer: (base) => ({ ...base, padding: '0 10px', flexWrap: 'nowrap' }),
        singleValue: (base) => ({ ...base, color: '#334155', fontWeight: 500 }),
        input: (base) => ({ ...base, margin: 0, padding: 0, color: '#334155' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, padding: '0 6px', color: '#94a3b8' }),
        menu: (base) => ({
            ...base,
            borderRadius: '10px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            overflow: 'hidden',
            zIndex: 20,
        }),
        menuList: (base) => ({ ...base, padding: '4px', maxHeight: '220px' }),
        option: (base, state) => ({
            ...base,
            borderRadius: '6px',
            fontSize: '0.78rem',
            padding: '7px 10px',
            color: state.isSelected ? '#fff' : '#334155',
            background: state.isSelected ? '#2463EB' : state.isFocused ? '#eff6ff' : 'transparent',
            cursor: 'pointer',
        }),
        placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '0.8rem' }),
    }

    const getReceiversForDailyReports = async () => {
        setIsFetching(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsGetReceivers', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setReceiversData(data?.response?.receivers ?? [])
            } else {
                if (data?.message === 'tokenExpired') authCtx.logout()
                else if (data?.response === null) setReceiversData([])
            }
        } catch { /* silent */ } finally {
            setIsFetching(false)
        }
    }

    const addReceiverForDailyReports = async () => {
        if (!receiver.trim()) { toast.error('Please enter a receiver.'); return }
        setIsAdding(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsAddReceiver', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    deliveryPlatform: activePlatform.title,
                    receiver,
                    receiverName: null,
                    receiverRole: 'owner',
                    countryCode: activePlatform.title === 'WhatsApp' ? countryCode.value : null,
                }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                setReceiver('')
                setCountryCode({ value: '+91', label: '+91 India' })
                getReceiversForDailyReports()
                toast.success('Receiver added successfully.')
            } else {
                if (data?.message === 'tokenExpired') authCtx.logout()
                else {
                    const errorMap = {
                        duplicateReceiver:    'This address is already configured for this store.',
                        receiverLimitReached: `Maximum ${data.response?.limit} receivers allowed per channel.`,
                        invalidEmailFormat:   'Please enter a valid email address.',
                        invalidPlatform:      'Invalid delivery channel selected.',
                        missingParams:        'Please fill in all required fields.',
                    }
                    toast.error(errorMap[data?.message] ?? 'Failed to add receiver.')
                }
            }
        } catch { toast.error('Something went wrong.') } finally {
            setIsAdding(false)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') addReceiverForDailyReports()
    }

    useEffect(() => {
        getReceiversForDailyReports()
    }, [activePlatform])

    const filteredReceivers = receiversData?.filter(
        (r) => r.deliveryPlatform === activePlatform?.title
    ) ?? []

    return (
        <div className={styles.mainWrapper}>
            {/* ── Page Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderIcon}>
                    <Rss size={20} />
                </div>
                <div>
                    <h1 className={styles.pageTitle}>Daily Reports</h1>
                    <p className={styles.pageSubtitle}>Configure where your daily store reports get delivered</p>
                </div>
            </div>

            {/* ── Platform Selection ── */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>Delivery Platform</div>
                    <div className={styles.cardSubtitle}>Choose the channel for your daily reports</div>
                </div>
                <div className={styles.platformGrid}>
                    {PLATFORMS.map((p) => {
                        const Icon = p.icon
                        const isActive = activePlatform?.title === p.title
                        return (
                            <button
                                key={p.title}
                                className={`${styles.platformCard} ${isActive ? styles.platformCardActive : ''}`}
                                style={isActive ? {
                                    '--p-bg': p.activeBg,
                                    '--p-border': p.activeBorder,
                                    '--p-color': p.activeColor,
                                } : {
                                    '--p-bg': p.bg,
                                    '--p-border': p.border,
                                    '--p-color': p.color,
                                }}
                                onClick={() => setActivePlatform(p)}
                                type="button"
                            >
                                <div className={styles.platformIconWrap}>
                                    <Icon size={22} color={isActive ? p.activeColor : p.color} />
                                </div>
                                <div className={styles.platformName}>{p.title}</div>
                                <div className={styles.platformDesc}>{p.description}</div>
                                {isActive && <CheckCircle2 size={16} className={styles.platformActiveDot} color={isActive ? p.activeColor : p.color} />}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* ── Receivers ── */}
            {activePlatform && (
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <div className={styles.cardHeaderRow}>
                            <div>
                                <div className={styles.cardTitle}>Receivers — {activePlatform.title}</div>
                                <div className={styles.cardSubtitle}>Manage who gets the daily report on {activePlatform.title}</div>
                            </div>
                            {!isFetching && filteredReceivers.length > 0 && (
                                <span className={styles.countBadge}>
                                    <Users size={11} />
                                    {filteredReceivers.length}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Add form */}
                    <div className={styles.addForm}>
                        {activePlatform.title === 'WhatsApp' && (
                            <div className={styles.countrySelectWrap}>
                                <Select
                                    options={countryCodeOptions}
                                    value={countryCode}
                                    onChange={(opt) => setCountryCode(opt)}
                                    styles={ccSelectStyles}
                                    isSearchable
                                    menuPlacement="auto"
                                    placeholder="Code…"
                                />
                            </div>
                        )}
                        <div className={styles.addInputWrap}>
                            {activePlatform.title === 'WhatsApp'
                                ? <MessageCircle size={15} color="#25D366" className={styles.inputIcon} />
                                : <Mail size={15} color="#2463EB" className={styles.inputIcon} />
                            }
                            <input
                                className={styles.addInput}
                                type={activePlatform.title === 'WhatsApp' ? 'number' : 'email'}
                                placeholder={activePlatform.title === 'WhatsApp' ? 'Enter 10-digit number…' : 'Enter email address…'}
                                value={receiver}
                                onChange={(e) => setReceiver(e.target.value)}
                                onKeyDown={handleKeyDown}
                                autoFocus
                                maxLength={activePlatform.title === 'WhatsApp' ? 10 : undefined}
                            />
                        </div>
                        <button
                            className={styles.addBtn}
                            onClick={addReceiverForDailyReports}
                            disabled={isAdding}
                            type="button"
                        >
                            {isAdding
                                ? <><Loader2 size={14} className={styles.spinIcon} /> Adding…</>
                                : <><Plus size={14} /> Add</>}
                        </button>
                    </div>

                    {/* List */}
                    <div className={styles.receiversList}>
                        {isFetching ? (
                            <div className={styles.loadingRow}>
                                <Loader2 size={16} className={styles.spinIcon} />
                                <span>Loading receivers…</span>
                            </div>
                        ) : filteredReceivers.length > 0 ? (
                            <>
                                {filteredReceivers.map((item, i) => (
                                    <DRCEachReceiver key={i} item={item} getReceiversForDailyReports={getReceiversForDailyReports} />
                                ))}
                                <p className={styles.thatsAll}>That’s all {filteredReceivers.length} receiver{filteredReceivers.length !== 1 ? 's' : ''}</p>
                            </>
                        ) : (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyIcon}>
                                    {activePlatform.title === 'WhatsApp'
                                        ? <MessageCircle size={28} color="#25D366" />
                                        : <Mail size={28} color="#2463EB" />}
                                </div>
                                <p className={styles.emptyTitle}>No receivers yet</p>
                                <p className={styles.emptySubtitle}>Add a {activePlatform.title} receiver above to start sending daily reports.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default DailyReportsConfiguration